import { AdminOnly } from '@/components/admin-only'
import { localeFor } from '@/lib/locale'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, Trash2, Sparkles, Tags, Wand2, Loader2, AlertTriangle, Link2, Download, Send, Mail } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useQuotes, type LineInput } from '@/hooks/use-quotes'
import { useInvoices } from '@/hooks/use-invoices'
import { useClients, clientName } from '@/hooks/use-clients'
import { usePricing } from '@/hooks/use-pricing'
import { useCompany } from '@/hooks/use-company'
import { money, taxRatePct } from '@/lib/format'
import { UnsavedGuard } from '@/components/unsaved-guard'
import { PageHeader, StatusPill, DetailSkeleton, DocumentBuilder, BuilderBack, btnPrimary, btnSecondary, hoverActions } from '@/components/rm'
import { cn } from '@/lib/utils'
import { ThreadStrip } from '@/components/thread-strip'
import { PriceListPicker } from '@/components/price-list-picker'
import { ProgressClaims } from '@/components/progress-claims'
import { QuoteAttachments } from '@/components/quote-attachments'
import { ActivityTimeline } from '@/components/activity-timeline'
import { useQuoteActivity } from '@/hooks/use-activity'
import { phFormat } from '@/lib/ph'
import { FEATURES } from '@/lib/features'

export const Route = createFileRoute('/_app/quotes_/$id')({ component: () => <AdminOnly><QuoteBuilder /></AdminOnly> })

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100
const cell = 'w-full bg-transparent px-2.5 py-2 text-[13px] text-token-primary outline-none rounded-md transition-colors focus:bg-token-surface focus:ring-1 focus:ring-token-border disabled:text-token-secondary'

function QuoteBuilder() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { quote, items, isLoadingDetail, save, aiDraft, convertToJob } = useQuotes(id)
  const { createFromQuote } = useInvoices()
  const { clients } = useClients()
  const qc = useQueryClient()
  const [sending, setSending] = useState(false)
  const sendQuote = async () => {
    setSending(true)
    const { data, error } = await supabase.functions.invoke('send-quote', { body: { quoteId: quote!.id, origin: window.location.origin } })
    setSending(false)
    if (error) {
      const ctx = (error as { context?: Response }).context
      const b = ctx && typeof ctx.json === 'function' ? await ctx.json().catch(() => null) : null
      return toast.error(b?.error ? (b.detail ? `${b.error}: ${b.detail}` : b.error) : 'Send failed')
    }
    toast.success(`Quote emailed to ${(data as { to?: string })?.to ?? 'client'}`)
    qc.invalidateQueries({ queryKey: ['quote', id] })
    qc.invalidateQueries({ queryKey: ['quotes'] })
  }
  const { items: catalogue } = usePricing()
  const { company } = useCompany()
  const gstRate = taxRatePct(company)

  const [loaded, setLoaded] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [clientId, setClientId] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [gst, setGst] = useState(false)
  const [lines, setLines] = useState<LineInput[]>([])
  const [deposit, setDeposit] = useState(0)
  const [assumptions, setAssumptions] = useState<string[]>([])
  const [brief, setBrief] = useState('')
  const [aiPhotos, setAiPhotos] = useState<string[]>([])   // base64 jpeg, no prefix

  const addAiPhoto = (file: File) => {
    const img = new Image()
    img.onload = () => {
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height))
      const c = document.createElement('canvas')
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale)
      c.getContext('2d')!.drawImage(img, 0, 0, c.width, c.height)
      const b64 = c.toDataURL('image/jpeg', 0.8).replace(/^data:image\/jpeg;base64,/, '')
      setAiPhotos((p) => [...p, b64].slice(0, 3))
      URL.revokeObjectURL(img.src)
    }
    img.src = URL.createObjectURL(file)
  }
  // Unsaved-changes tracking (spec 005): signature of the editable document.
  const savedSig = useRef('')
  const sigOf = (v: unknown) => JSON.stringify(v)

  const [aiOpen, setAiOpen] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)

  if (quote && quote.id !== loaded) {
    const loadedTitle = quote.title ?? ''
    const loadedClientId = quote.client_id ?? ''
    const loadedValidUntil = quote.valid_until ?? ''
    const loadedGst = (quote.tax ?? 0) > 0
    const loadedDeposit = Number((quote as { deposit_amount?: number }).deposit_amount ?? 0)
    const loadedLines: LineInput[] = items.length
      ? items.map((it) => ({
          description: it.description, quantity: Number(it.quantity), unit_price: Number(it.unit_price),
          kind: it.kind ?? 'material', unit: it.unit ?? 'ea', cost: Number(it.cost ?? 0), tax_code: it.tax_code ?? 'GST',
          source: it.source ?? 'manual', needs_price: it.needs_price ?? false, price_item_id: it.price_item_id ?? null,
          optional: it.optional ?? false, tier: it.tier ?? null,
        }))
      : [{ description: '', quantity: 1, unit_price: 0, source: 'manual' }]

    setLoaded(quote.id)
    setTitle(loadedTitle)
    setClientId(loadedClientId)
    setValidUntil(loadedValidUntil)
    setGst(loadedGst)
    setDeposit(loadedDeposit)
    setLines(loadedLines)
    savedSig.current = sigOf({
      title: loadedTitle, clientId: loadedClientId, validUntil: loadedValidUntil,
      gst: loadedGst, deposit: loadedDeposit, lines: loadedLines,
    })
  }

  if (isLoadingDetail && !quote) {
    return <div className="min-h-screen bg-canvas-subtle p-10"><DetailSkeleton /></div>
  }
  if (!quote) {
    return (
      <div className="min-h-screen bg-canvas-subtle p-10">
        <PageHeader title="Quote not found" />
        <button className={btnSecondary + ' mt-4'} onClick={() => navigate({ to: '/quotes' })}><ArrowLeft className="h-4 w-4" /> Back to quotes</button>
      </div>
    )
  }

  // Compared case-insensitively on purpose. A row stored as "Draft" rather than "draft" used to fall
  // through every branch below, leaving the quote silently read-only: no Save, no Send, no Add line,
  // every field disabled, while StatusPill still rendered "Draft" via CSS capitalize. Indistinguishable
  // from a working quote. The database now rejects mixed case, and both mobile apps already lowercase
  // before comparing; this keeps the web app honest if a row ever gets in by another route.
  const status = (quote.status ?? '').toLowerCase()
  const editable = status === 'draft' || status === 'sent'
  // Spec 037 FR-004c. `inbound-quote-email` stamps sender_confident=false when the enquiry looked
  // forwarded and no rung of the resolution ladder could name the original sender, so the quote is
  // filed against the FORWARDER. We warn until the selected client is somebody other than that
  // address — comparing emails rather than tracking a "dismissed" flag, so the warning is driven by
  // whether the problem is still there and survives a reload either way.
  const fallbackEmail = String(
    (quote.ai_meta as { from?: unknown } | null | undefined)?.from ?? '',
  ).toLowerCase()
  const unsureSender =
    editable &&
    (quote.ai_meta as { sender_confident?: unknown } | null | undefined)?.sender_confident === false &&
    !!fallbackEmail &&
    (clients.find((c) => c.id === clientId)?.email ?? '').toLowerCase() === fallbackEmail

  const currentSig = sigOf({ title, clientId, validUntil, gst, deposit, lines })
  const isDirty = editable && loaded === quote.id && currentSig !== savedSig.current
  const subtotal = round2(lines.reduce((s, l) => s + (l.quantity || 0) * (l.unit_price || 0), 0))
  const tax = gst ? round2(subtotal * (gstRate / 100)) : 0
  const total = round2(subtotal + tax)
  const cur = quote.currency
  const needsPrice = lines.some((l) => l.needs_price)
  const setLine = (i: number, p: Partial<LineInput>) => setLines(lines.map((l, idx) => (idx === i ? { ...l, ...p } : l)))

  const doSave = async () => {
    try {
      await save.mutateAsync({
        id: quote.id, title: title.trim() || null, client_id: clientId || null,
        valid_until: validUntil || null, items: lines.filter((l) => l.description.trim()), applyGst: gst,
        deposit_amount: deposit,
      })
      savedSig.current = currentSig
      toast.success('Saved')
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }

  const runAi = async () => {
    if (!brief.trim() && aiPhotos.length === 0) return toast.error('Describe the job or add a photo first.')
    try {
      const r = await aiDraft.mutateAsync({ brief, gstRate, images: aiPhotos })
      const drafted = r.line_items.map((li) => ({
        description: li.description, quantity: li.quantity, unit_price: li.unit_price,
        kind: li.kind ?? 'material', unit: li.unit ?? 'ea', cost: li.cost ?? 0, tax_code: li.tax_code ?? 'GST',
        source: 'ai' as const, needs_price: li.needs_price ?? false, price_item_id: li.price_item_id ?? null,
      }))
      // Replace blank starter row; otherwise append the AI draft.
      const base = lines.filter((l) => l.description.trim())
      setLines([...base, ...drafted])
      setAssumptions(r.assumptions ?? [])
      setGst(true)
      setAiOpen(false)
      toast.success(`AI drafted ${drafted.length} line${drafted.length === 1 ? '' : 's'}${r.ai_meta?.needs_price_count ? ` · ${r.ai_meta.needs_price_count} need pricing` : ''}`)
    } catch (e) { toast.error(e instanceof Error ? e.message : 'AI draft failed') }
  }

  const addFromCatalogue = (pi: (typeof catalogue)[number]) => {
    setLines([...lines.filter((l) => l.description.trim() || lines.length === 1 && !l.description), {
      description: pi.name, quantity: 1, unit_price: Number(pi.sell_price), kind: pi.kind, unit: pi.unit ?? 'ea',
      cost: Number(pi.cost ?? 0), tax_code: pi.tax_code ?? 'GST', source: 'catalogue', needs_price: false, price_item_id: pi.id,
    }])
    setPickerOpen(false)
  }

  return (
    <DocumentBuilder
      back={<BuilderBack label="Quotes" onClick={() => navigate({ to: '/quotes' })} />}
      strip={<ThreadStrip anchor={{ quoteId: quote.id }} current="quote" />}
      actions={
        <>
            <StatusPill status={quote.status} />
            {quote.share_token && (
              <>
                <button className={btnSecondary} onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/q/${quote.share_token}`)
                  toast.success('Customer link copied')
                }}><Link2 className="h-4 w-4" /> Copy link</button>
                <button className={btnSecondary} onClick={() => window.open(`/q/${quote.share_token}`, '_blank')}>
                  <Download className="h-4 w-4" /> PDF
                </button>
              </>
            )}
            {editable && <button className={btnSecondary} onClick={doSave} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</button>}
            {(status === 'draft' || status === 'sent' || status === 'viewed') && (
              <button className={btnPrimary} onClick={sendQuote} disabled={sending}>
                <Send className="h-4 w-4" /> {sending ? 'Sending…' : status === 'draft' ? 'Send to client' : 'Resend'}
              </button>
            )}
            {editable && (
              <button className={btnSecondary} onClick={async () => {
                await save.mutateAsync({ id: quote.id, title: title.trim() || null, client_id: clientId || null, valid_until: validUntil || null, items: lines.filter((l) => l.description.trim()), applyGst: gst, deposit_amount: deposit })
                savedSig.current = currentSig
                const r = await convertToJob.mutateAsync({ quote, title: title.trim() || `Job from ${quote.quote_number}` })
                toast.success('Accepted — job created')
                navigate({ to: '/jobs/$id', params: { id: String(r.jobId) } })
              }} disabled={convertToJob.isPending}>
                {convertToJob.isPending ? 'Converting…' : 'Accept → Job'}
              </button>
            )}
            {status === 'accepted' && (
              <button className={btnPrimary} onClick={async () => {
                const r = await createFromQuote.mutateAsync({ quoteId: quote.id })
                savedSig.current = currentSig
                toast.success('Invoice drafted from quote')
                navigate({ to: '/invoices/$id', params: { id: String(r.invoiceId) } })
              }} disabled={createFromQuote.isPending}>
                {createFromQuote.isPending ? 'Creating…' : 'Create invoice'}
              </button>
            )}
        </>
      }
    >
      <UnsavedGuard when={isDirty} />
      <div>
        {/* Provenance — shown when the quote was created from a forwarded email */}
        <EmailSource meta={quote.ai_meta} />

        {/* 056 — whatever the customer sent with the enquiry. Directly under the email it came
            with, because that is the context that explains it. */}
        <QuoteAttachments quoteId={quote.id} contactId={quote.client_id ?? null} editable={editable} />

        {/* 011 US1 — every automated touch on this quote, in order. */}
        <QuoteActivity quoteId={quote.id} />

        {/* Spec 025 — staged invoicing. Only on an accepted quote: an unaccepted quote is not yet
            a contract, so there is nothing to claim against. */}
        {status === 'accepted' && (
          <ProgressClaims quote={{ id: quote.id, subtotal: Number(quote.subtotal ?? 0), tax: Number(quote.tax ?? 0) }} />
        )}

        {/* AI draft panel */}
        {editable && (
          <div className="mb-5 overflow-hidden rounded-[14px] border border-token-warning bg-gradient-to-br from-token-warning-surface to-white">
            <button onClick={() => setAiOpen((o) => !o)} className="flex w-full items-center justify-between px-5 py-3.5 text-left">
              <span className="inline-flex items-center gap-2 text-[14px] font-bold text-token-primary"><Sparkles className="h-4 w-4 text-token-primary" /> Draft with AI</span>
              <span className="text-[12.5px] font-semibold text-token-warning">{aiOpen ? 'Hide' : 'Describe the job →'}</span>
            </button>
            {aiOpen && (
              <div className="border-t border-token-warning px-5 py-4">
                <textarea
                  value={brief} onChange={(e) => setBrief(e.target.value)} rows={3}
                  placeholder="e.g. Replace a 12-way switchboard in a single-storey house, install 2 new circuits and a safety switch. Half a day, one electrician + apprentice."
                  className="w-full rounded-[10px] border border-token-subtle bg-token-surface px-3 py-2.5 text-[13.5px] text-token-primary outline-none focus:border-token-strong"
                />
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-[9px] border border-token-subtle bg-token-surface px-3 py-1.5 text-[12.5px] font-semibold text-token-secondary hover:border-token-strong hover:text-token-primary">
                    📷 Add photo{aiPhotos.length > 0 ? ` (${aiPhotos.length}/3)` : ''}
                    <input type="file" accept="image/*" className="hidden" disabled={aiPhotos.length >= 3}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) addAiPhoto(f); e.target.value = '' }} />
                  </label>
                  {aiPhotos.map((b, i) => (
                    <span key={i} className="relative inline-block">
                      <img src={`data:image/jpeg;base64,${b}`} alt="" className="h-10 w-10 rounded-[8px] border border-token-subtle object-cover" />
                      <button onClick={() => setAiPhotos(aiPhotos.filter((_, j) => j !== i))}
                        className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-brand text-[9px] font-bold text-brand-fg">✕</button>
                    </span>
                  ))}
                </div>
                <div className="mt-2.5 flex items-center justify-between">
                  <p className="text-[12px] text-token-secondary">Grounded in your {catalogue.length}-item price list. The AI itemises what it sees in photos — every line stays editable.</p>
                  <button className={btnPrimary} onClick={runAi} disabled={aiDraft.isPending}>
                    {aiDraft.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> Estimating…</> : <><Wand2 className="h-4 w-4" /> Generate quote</>}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Document */}
        <div className="rounded-[14px] border border-token-subtle bg-token-surface p-4 shadow-[0_1px_3px_rgba(16,24,40,0.06)] sm:p-8 lg:p-10">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-token-muted">Quote</p>
              <input value={title} disabled={!editable} onChange={(e) => setTitle(e.target.value)} placeholder="Quote title"
                className="font-display -ml-1 mt-1 w-full rounded-md bg-transparent px-1 text-[22px] font-bold tracking-[-0.02em] text-token-primary outline-none focus:bg-token-surface" />
            </div>
            <p className="flex-none text-[13px] font-bold tabular-nums text-token-primary">{quote.quote_number}</p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 border-t border-token-subtle pt-6 sm:grid-cols-2 sm:gap-8">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">To</p>
              {/*
                Spec 037 FR-004c. This quote came in by email and we could NOT read the original
                sender, so the client below is whoever forwarded it. The picker was always here;
                what was missing was any hint that it needs changing. Silence would mean the quote
                goes back to the trade person rather than to their customer.
              */}
              {unsureSender && (
                <p className="mb-1.5 mt-1 rounded-md bg-token-warning-surface px-2 py-1.5 text-[12px] leading-snug text-token-warning ring-1 ring-token-warning">
                  This came in as a forwarded email and we couldn’t tell who originally sent it, so
                  this is the forwarding address. Pick the real client before you send.
                </p>
              )}
              <select value={clientId} disabled={!editable} onChange={(e) => setClientId(e.target.value)}
                className="-ml-1 mt-1.5 w-full rounded-md bg-transparent px-1 py-1 text-[14px] font-semibold text-token-primary outline-none focus:bg-token-surface">
                <option value="">Select a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{clientName(c)}</option>)}
              </select>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Expiry</p>
              <input type="date" value={validUntil} disabled={!editable} onChange={(e) => setValidUntil(e.target.value)}
                className="mt-1.5 rounded-md bg-transparent px-1 py-1 text-right text-[14px] font-semibold text-token-primary outline-none focus:bg-token-surface" />
            </div>
          </div>

          {/* Assumptions (AI) */}
          {assumptions.length > 0 && (
            <div className="mt-6 rounded-[12px] border border-token-subtle bg-token-surface p-4">
              <p className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-secondary"><Sparkles className="h-3.5 w-3.5 text-token-primary" /> Estimating assumptions</p>
              <ul className="space-y-1.5">
                {assumptions.map((a, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <input value={a} disabled={!editable} onChange={(e) => setAssumptions(assumptions.map((x, idx) => idx === i ? e.target.value : x))}
                      className="flex-1 rounded-md bg-transparent px-1 py-0.5 text-[13px] text-token-secondary outline-none focus:bg-token-surface" />
                    {editable && <button onClick={() => setAssumptions(assumptions.filter((_, idx) => idx !== i))} className="text-token-muted hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Lines */}
          <div className="mt-8 overflow-x-auto">
            <table className="w-full min-w-[480px] border-separate border-spacing-0">
              <thead>
                <tr className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">
                  <th className="border-b border-token-subtle px-2.5 pb-2 text-left">Description</th>
                  <th className="w-16 border-b border-token-subtle px-2.5 pb-2 text-right">Qty</th>
                  <th className="w-28 border-b border-token-subtle px-2.5 pb-2 text-right">Unit price</th>
                  <th className="w-28 border-b border-token-subtle px-2.5 pb-2 text-right">Amount</th>
                  <th className="w-8 border-b border-token-subtle" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="group">
                    <td className="border-b border-token-subtle">
                      <div className="flex items-center gap-1.5 px-1">
                        <input value={l.description} disabled={!editable} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Description of work…" className={cell} />
                        {l.source === 'ai' && <span className="flex-none rounded bg-token-selected px-1.5 py-0.5 text-[10px] font-bold text-token-warning">AI</span>}
                        {l.needs_price && <span className="flex-none inline-flex items-center gap-0.5 rounded bg-token-danger-surface px-1.5 py-0.5 text-[10px] font-bold text-token-danger"><AlertTriangle className="h-2.5 w-2.5" /> price</span>}
                      </div>
                    </td>
                    <td className="border-b border-token-subtle"><input type="number" value={Number.isFinite(l.quantity) ? l.quantity : ''} disabled={!editable} onChange={(e) => setLine(i, { quantity: e.target.valueAsNumber || 0 })} className={cell + ' text-right'} /></td>
                    <td className="border-b border-token-subtle"><input type="number" value={Number.isFinite(l.unit_price) ? l.unit_price : ''} disabled={!editable} onChange={(e) => setLine(i, { unit_price: e.target.valueAsNumber || 0, needs_price: false })} className={cell + ' text-right'} /></td>
                    <td className="border-b border-token-subtle px-2.5 text-right text-[13px] font-semibold tabular-nums text-token-primary">{money((l.quantity || 0) * (l.unit_price || 0), cur)}</td>
                    <td className="border-b border-token-subtle text-center">
                      <div className="flex items-center justify-end gap-1">
                        {editable && (
                          <>
                            {/* Optional and tier are mutually exclusive: a tier IS the choice, so an
                                optional line inside one would be a choice within a choice. */}
                            <button
                              title={l.optional ? 'Optional extra — click to make it standard' : 'Make this an optional extra'}
                              onClick={() => setLine(i, { optional: !l.optional, tier: null })}
                              className={cn('rounded px-1.5 py-0.5 text-[10px] font-bold',
                                l.optional ? 'bg-token-info-surface text-token-info' : cn('text-token-muted hover:text-token-secondary', hoverActions))}
                            >
                              OPT
                            </button>
                            <select
                              value={l.tier ?? ''}
                              onChange={(e) => setLine(i, { tier: e.target.value || null, optional: false })}
                              title="Put this line in a good/better/best option"
                              className={cn('rounded border-0 bg-transparent px-0.5 py-0.5 text-[10px] font-bold outline-none',
                                l.tier ? 'text-token-info' : cn('text-token-muted', hoverActions))}
                            >
                              <option value="">—</option>
                              <option value="good">GOOD</option>
                              <option value="better">BETTER</option>
                              <option value="best">BEST</option>
                            </select>
                            <button onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className={cn('text-token-muted hover:text-rose-500', hoverActions)}><Trash2 className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                        {!editable && (l.optional || l.tier) && (
                          <span className="text-[10px] font-bold text-token-secondary">{l.optional ? 'OPT' : l.tier?.toUpperCase()}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {editable && (
              <div className="mt-2 flex items-center gap-4">
                <button onClick={() => setLines([...lines, { description: '', quantity: 1, unit_price: 0, source: 'manual' }])} className="inline-flex items-center gap-1.5 px-2.5 text-[13px] font-semibold text-token-primary hover:text-token-primary"><Plus className="h-3.5 w-3.5" /> Add a line</button>
                <button onClick={() => setPickerOpen(true)} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-token-secondary hover:text-token-primary"><Tags className="h-3.5 w-3.5" /> From price list</button>
              </div>
            )}
          </div>

          {needsPrice && (
            <p className="mt-4 inline-flex items-center gap-1.5 rounded-[8px] bg-token-danger-surface px-3 py-1.5 text-[12.5px] font-semibold text-token-danger"><AlertTriangle className="h-3.5 w-3.5" /> Some lines need a price before sending.</p>
          )}

          {/* Totals */}
          <div className="mt-8 flex justify-end">
            <div className="w-full max-w-[280px] space-y-2">
              <div className="flex items-center justify-between text-[13px]"><span className="text-token-secondary">Subtotal</span><span className="tabular-nums text-token-primary">{money(subtotal, cur)}</span></div>
              <label className="flex items-center justify-between text-[13px] text-token-secondary">
                <span className="inline-flex items-center gap-2"><input type="checkbox" checked={gst} disabled={!editable} onChange={(e) => setGst(e.target.checked)} className="h-3.5 w-3.5 accent-brand" /> {localeFor(company).taxLabel} {gstRate}%</span>
                <span className="tabular-nums text-token-primary">{money(tax, cur)}</span>
              </label>
              <div className="flex items-center justify-between border-t border-token-subtle pt-2 text-[16px] font-bold"><span className="text-token-primary">Total</span><span className="font-display tabular-nums text-token-primary">{money(total, cur)}</span></div>
              {/* A deposit is paid by card through Stripe, which MySerbisyo does not use (spec 001). */}
              {FEATURES.stripePayments && (
              <div className="flex items-center justify-between text-[13px]">
                <span className="inline-flex items-center gap-1.5 text-token-secondary">Deposit
                  {Number(company?.deposit_default_pct ?? 0) > 0 && editable && (
                    <button type="button" onClick={() => setDeposit(round2(total * Number(company!.deposit_default_pct!) / 100))}
                      className="rounded-full border border-token-subtle px-2 py-0.5 text-[10.5px] font-bold text-token-secondary hover:border-token-strong hover:text-token-primary">{company!.deposit_default_pct}%</button>
                  )}
                </span>
                <span className="inline-flex items-center gap-1 tabular-nums text-token-primary">₱
                  <input type="number" min={0} step={10} value={deposit || ''} placeholder="0" disabled={!editable}
                    onChange={(e) => setDeposit(Math.max(0, Number(e.target.value) || 0))}
                    className="h-7 w-[84px] rounded-[7px] border border-token-subtle px-2 text-right text-[13px] outline-none focus:border-token-strong disabled:bg-token-surface" />
                </span>
              </div>
              )}
              {(() => {
                const cost = round2(lines.reduce((s, l) => s + (l.quantity || 0) * (Number(l.cost) || 0), 0))
                if (cost <= 0 || subtotal <= 0) return null
                const marginAmt = round2(subtotal - cost)
                const pct = Math.round((marginAmt / subtotal) * 100)
                // The floor is a nudge, not a gate (spec 032 US4): sending stays one tap.
                const target = company?.target_margin_pct ?? null
                const below = target != null && pct < target
                return (
                  <div className={below
                    ? 'rounded-[8px] bg-token-danger-surface px-2.5 py-1.5 text-[12px] font-semibold'
                    : 'flex items-center justify-between rounded-[8px] bg-token-selected px-2.5 py-1.5 text-[12px] font-semibold'}>
                    {below ? (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-token-danger">Est. margin</span>
                          <span className="tabular-nums text-token-danger">{money(marginAmt, cur)} · {pct}%</span>
                        </div>
                        <p className="mt-0.5 font-normal text-token-danger">Below your {target}% target</p>
                      </>
                    ) : (
                      <>
                        <span className="text-token-success">Est. margin</span>
                        <span className={marginAmt < 0 ? 'tabular-nums text-token-danger' : 'tabular-nums text-token-success'}>{money(marginAmt, cur)} · {pct}%</span>
                      </>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      </div>

      <PriceListPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        catalogue={catalogue}
        currency={cur}
        onPick={addFromCatalogue}
      />
    </DocumentBuilder>
  )
}

/** Shows the original email a quote was auto-created from (source provenance). */
function EmailSource({ meta }: { meta: Record<string, unknown> | null | undefined }) {
  const [open, setOpen] = useState(false)
  const m = meta as { source?: string; email?: { from?: string; fromName?: string; subject?: string; text?: string; received_at?: string; attachments?: string[] } } | null
  const e = m?.source === 'inbound_email' ? m.email : null
  if (!e) return null
  const when = e.received_at ? phFormat(new Date(e.received_at), 'datetime', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''
  return (
    <div className="mb-5 overflow-hidden rounded-[14px] border border-token-subtle bg-token-surface">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between px-5 py-3.5 text-left">
        <span className="inline-flex items-center gap-2 text-[14px] font-bold text-token-primary">
          <span className="flex h-6 w-6 items-center justify-center rounded-[7px] bg-token-selected"><Mail className="h-3.5 w-3.5 text-token-primary" /></span>
          Created from an email
        </span>
        <span className="text-[12.5px] font-semibold text-token-secondary">{open ? 'Hide email' : 'View email'}</span>
      </button>
      <div className="border-t border-token-subtle px-5 py-3 text-[13px]">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-token-secondary">
          <span><strong className="text-token-primary">From:</strong> {e.fromName ? `${e.fromName} · ` : ''}{e.from}</span>
          {when && <span><strong className="text-token-primary">Received:</strong> {when}</span>}
        </div>
        {e.subject && <div className="mt-1 text-token-secondary"><strong className="text-token-primary">Subject:</strong> {e.subject}</div>}
        {open && (
          <>
            <div className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap rounded-[10px] bg-token-surface p-3 text-[13px] leading-relaxed text-token-primary">{e.text || '(no message body)'}</div>
            {e.attachments?.length ? <div className="mt-2 text-[12px] text-token-secondary">📎 {e.attachments.join(', ')}</div> : null}
          </>
        )}
      </div>
    </div>
  )
}

/** 011 T005. Its own component so the activity query does not re-render the whole builder. */
function QuoteActivity({ quoteId }: { quoteId: number }) {
  const { data = [], isLoading } = useQuoteActivity(quoteId)
  return <ActivityTimeline entries={data} isLoading={isLoading} />
}
